import type { MarketData } from '@/hooks/useMarketData';
import type {
  AssetCategory,
  MarketingScript,
  NewsItem,
  ScriptCitation,
} from '@/types';

type NewsSelection = {
  items: NewsItem[];
  citations: ScriptCitation[];
};

const CATEGORY_KEYWORDS: Record<AssetCategory, string[]> = {
  宏观经济: ['央行', 'mlf', '降准', '降息', '财政', '稳增长', '政策', '流动性', '经济', '利率'],
  股市: ['a股', '上证', '深证', '创业板', '指数', '北向', '外资', '科技股', '医药', '震荡', '成交', '资金'],
  债市: ['国债', '债市', '债券', '收益率', '信用债', '城投', '房企', '利率', '资金面', '久期'],
  基金: ['基金', 'etf', '公募', '定投', '指数', '净申购', '主动', '被动', '宽基'],
  理财: ['理财', '现金管理', '收益率', '到期', '固收', '监管', '产品'],
  保险: ['保险', '险资', '保障', '传承', '权益仓位', '长期'],
  贵金属: ['黄金', '白银', '金银比', '贵金属', '美元/盎司', '购金', '避险'],
};

const CATEGORY_GUIDANCE: Record<AssetCategory, string> = {
  宏观经济: '政策方向、经济预期和客户整体资产配置的影响',
  股市: '客户的持仓波动、投资期限以及分散配置的必要性',
  债市: '产品净值波动、久期选择和资金的流动性安排',
  基金: '主动与被动产品的差异、持有期限和分批投入方式',
  理财: '产品期限、收益波动和客户近期的资金使用计划',
  保险: '长期保障、现金流安排和产品流动性边界',
  贵金属: '避险需求、仓位比例和分批配置而不是追涨',
};

const NEWS_NOT_READY_FOR_CLIENTS = /中标|签署合同|公司公告|个股异动|股权转让|专利|募资/;
const MAX_NEWS_AGE_MS = 72 * 60 * 60 * 1000;

function marketSnapshot(market: MarketData | null) {
  if (!market) return '';

  const quoteLines: string[] = [];
  if (market.sh) {
    quoteLines.push(`上证综指${market.sh.price.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}点，${market.sh.changePct}%`);
  }
  if (market.sz) {
    quoteLines.push(`深证成指${market.sz.price.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}点，${market.sz.changePct}%`);
  }
  if (market.cyb) {
    quoteLines.push(`创业板指${market.cyb.price.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}点，${market.cyb.changePct}%`);
  }

  return quoteLines.length > 0 ? `今天${quoteLines.join('；')}。` : '';
}

function scoreNews(item: NewsItem, categories: AssetCategory[]) {
  const title = item.title.toLowerCase();
  const text = `${title} ${item.summary} ${item.content}`.toLowerCase();
  const publishedAt = Date.parse(item.publishedAt);
  const now = Date.now();

  if (
    !categories.includes(item.category) ||
    NEWS_NOT_READY_FOR_CLIENTS.test(text) ||
    !Number.isFinite(publishedAt) ||
    publishedAt > now + 5 * 60 * 1000 ||
    now - publishedAt > MAX_NEWS_AGE_MS
  ) {
    return 0;
  }

  const keywordScore = CATEGORY_KEYWORDS[item.category].reduce((score, keyword) => {
    const normalizedKeyword = keyword.toLowerCase();
    return score + (text.includes(normalizedKeyword) ? 2 : 0) + (title.includes(normalizedKeyword) ? 1 : 0);
  }, 0);

  return keywordScore >= 2 ? 4 + keywordScore : 0;
}

function selectNews(
  items: NewsItem[],
  categories: AssetCategory[],
  max: number,
  threshold = 7,
): NewsSelection {
  const ranked = items
    .filter(item => item.url)
    .map((item, index) => ({ item, index, score: scoreNews(item, categories) }))
    .filter(candidate => candidate.score >= threshold)
    .sort((a, b) =>
      b.score - a.score ||
      Date.parse(b.item.publishedAt) - Date.parse(a.item.publishedAt) ||
      a.index - b.index,
    );
  const selected: NewsItem[] = [];

  for (const candidate of ranked) {
    if (selected.some(item => item.id === candidate.item.id)) continue;
    const hasSameCategory = selected.some(item => item.category === candidate.item.category);
    if (hasSameCategory && categories.length > 1) continue;
    selected.push(candidate.item);
    if (selected.length === max) break;
  }

  return {
    items: selected,
    citations: selected.map(item => ({
      title: item.title,
      source: item.source,
      publishedAt: item.publishedAt,
      url: item.url!,
    })),
  };
}

function newsDigest(selection: NewsSelection, fallback: string) {
  if (selection.items.length === 0) return fallback;
  if (selection.items.length === 1) {
    const item = selection.items[0];
    return `今天有个可以和您分享的变化：${item.title}。${item.summary || item.content}`;
  }

  return `今天有几条信息放在一起看比较有意义：${selection.items
    .map((item, index) => `\n${index + 1}. ${item.title}。${item.summary || item.content}`)
    .join('')}`;
}

function newsKeyPoint(selection: NewsSelection) {
  return selection.items.length > 0
    ? `自然引用${selection.items.length}条今日要闻，作为沟通切入点`
    : '不依赖单条新闻，先从客户真实需求开始沟通';
}

function buildDailyScript(
  id: string,
  title: string,
  category: AssetCategory | '通用',
  scenario: string,
  script: string,
  keyPoints: string[],
  selection: NewsSelection = { items: [], citations: [] },
): MarketingScript {
  return {
    id,
    title,
    category,
    scenario,
    script,
    keyPoints: [newsKeyPoint(selection), ...keyPoints],
    sourceNews: selection.citations,
    isDaily: true,
  };
}

function buildMorningScript(selection: NewsSelection) {
  return buildDailyScript(
    `daily-general-${selection.items.map(item => item.id).join('-') || 'client-focus'}`,
    '今日晨会开场：先聊客户关心的事',
    '通用',
    '晨会后向客户发送当天市场重点，但不把早报变成产品推销。',
    `早上好，今天想和您同步一下市场情况。${newsDigest(selection, '今天市场没有特别适合用来下结论的单一事件，我们更建议把关注点放回您的资金安排和持仓变化。')}这些信息不代表马上要买入或卖出，真正重要的是它和您的资产、投资期限有什么关系。您今天更想了解市场变化，还是想一起看看现有配置有没有需要调整的地方？`,
    [
      '先提供信息价值，再询问客户真正关心的方向',
      '把新闻与持仓、期限和风险承受能力联系起来',
      '不把晨报写成确定涨跌或产品推荐',
    ],
    selection,
  );
}

export function generateDailyScripts(
  items: NewsItem[],
  market: MarketData | null,
): MarketingScript[] {
  if (items.length === 0) return [];

  const morningNews = selectNews(items, ['宏观经济', '股市', '贵金属'], 3);
  const stockNews = selectNews(items, ['股市'], 1);
  const allocationNews = selectNews(items, ['宏观经济', '债市', '股市'], 2);
  const fundNews = selectNews(items, ['基金'], 1);
  const goldNews = selectNews(items, ['贵金属'], 1);
  const bondNews = selectNews(items, ['债市'], 1);
  const stockMarket = marketSnapshot(market);

  return [
    buildMorningScript(morningNews),
    buildDailyScript(
      'daily-stock-client-care',
      '股市客户维护：先安抚波动，再做资产回顾',
      '股市',
      '客户担心权益市场波动，或者持仓客户一段时间没有联系。',
      `您好，最近如果您比较关注权益市场，我想先和您做个小结。${newsDigest(stockNews, '最近市场仍有波动，单看一天的涨跌很难判断方向。')}${stockMarket ? ` ${stockMarket}` : ''}我们不急着用一条新闻判断买卖，更建议先看您的持仓集中度、资金使用时间和能承受多大回撤。如果您近期没有用钱计划，可以考虑把调整拆成几步；如果对波动比较敏感，我们也可以先做一次配置回顾。您现在最担心的是本金波动，还是错过后面的机会？`,
      [
        '先回应客户的担忧，不直接给出买卖结论',
        '结合持仓、期限和可承受回撤安排下一步',
        '用资产回顾替代强行推荐，保留继续沟通空间',
      ],
      stockNews,
    ),
    buildDailyScript(
      'daily-allocation-conversation',
      '闲钱配置沟通：先分用途，再谈收益',
      '宏观经济',
      '客户手里有一笔闲钱，正在比较存款、固收和权益类配置。',
      `您好，如果您最近有一笔闲钱想做安排，我建议先不急着比较哪个产品收益最高。${newsDigest(allocationNews, '当前市场环境下，不同资产的波动和流动性差异仍然比较明显。')}更稳妥的做法是先把这笔钱按使用时间分开：短期要用的留足流动性，中期资金考虑稳健资产，较长期的部分再讨论权益或分批配置。这样既不会因为追逐短期热点影响用钱，也能保留一定的增值空间。您这笔资金大概多久不会用到？`,
      [
        '先问资金用途和使用时间，再讨论产品',
        `围绕${CATEGORY_GUIDANCE.宏观经济}做解释`,
        '用分层配置降低客户一次性做决定的压力',
      ],
      allocationNews,
    ),
    buildDailyScript(
      'daily-fund-staged-investing',
      '基金沟通：不想选股，可以先从分批投入开始',
      '基金',
      '客户看好长期市场，但担心选错基金或一次性买在高点。',
      `如果您想参与市场、又不想每天研究个股，可以先考虑分批投入，而不是一次性押注。${newsDigest(fundNews, '基金产品之间的差异很大，选择时更应该看投资范围、波动和持有期限。')}比如把计划投入的资金拆成几份，按月或按阶段投入宽基指数类产品，同时保留应急资金。定投也不是稳赚不赔，市场下跌时仍会有账面波动，所以我们要先确认您能持有多久、能承受多大回撤。您更偏向长期积累，还是近期有明确的资金目标？`,
      [
        '用分批投入降低一次性择时压力',
        '提醒基金仍有波动，不承诺收益',
        '确认持有期限和风险承受能力',
      ],
      fundNews,
    ),
    buildDailyScript(
      'daily-gold-position',
      '黄金沟通：关注配置比例，不追着涨跌走',
      '贵金属',
      '客户看到金价变化，主动询问现在是否适合买黄金。',
      `您关注黄金很及时，但黄金上涨并不等于现在应该一次性买入。${newsDigest(goldNews, '贵金属价格容易受到利率、汇率和避险情绪影响，短期波动可能比较大。')}如果您的目的是分散风险，可以先看现有贵金属占整体资产的比例，再考虑用黄金积存等方式分批配置；如果是短期想赚价差，就要把可能的回撤和资金占用说清楚。您现在更看重避险分散，还是希望追求短期收益？`,
      [
        '把黄金从“追涨”话题转回资产配置比例',
        '优先讨论分批配置和资金占用',
        '区分避险需求与短期交易需求',
      ],
      goldNews,
    ),
    buildDailyScript(
      'daily-bond-volatility',
      '债基沟通：净值波动时先核对资金期限',
      '债市',
      '持有债券基金或固收产品的客户看到净值波动，担心是否要赎回。',
      `您好，债基出现短期净值波动时，先不用急着决定赎回。${newsDigest(bondNews, '债券产品也会受到利率变化、信用风险和持有期限影响，短期净值不一定代表最终收益。')}我们先核对这笔钱什么时候要用、产品主要投向和您能接受的波动。如果是近期要用的资金，应优先考虑流动性；如果期限较长，再评估是否需要调整，而不是在情绪紧张时一次性操作。您这笔资金近期有明确用款安排吗？`,
      [
        '先核对资金期限和产品底层资产',
        '解释债券产品并非完全没有波动',
        '不在客户焦虑时直接建议加仓或赎回',
      ],
      bondNews,
    ),
    buildDailyScript(
      'daily-wealth-maturity',
      '理财到期续作：先确认近期用钱计划',
      '理财',
      '客户理财产品即将到期，需要沟通续作方向。',
      '您好，您这笔理财产品快到期了，我提前和您确认一下安排。我们可以根据未来几个月的用钱计划，比较继续配置稳健型产品、缩短期限，或者保留一部分流动资金，而不是只看当下的预期收益率。您近期有没有大额支出、买房买车或其他资金安排？确认期限后，我再给您准备两三个合适的方向。',
      [
        '主动提前联系，体现服务而不是推销',
        '先确认流动性，再比较期限和收益',
        '给客户选择空间，不用单一产品替代需求判断',
      ],
    ),
    buildDailyScript(
      'daily-insurance-protection',
      '保险沟通：从家庭保障缺口开始',
      '保险',
      '客户有家庭责任或资产传承需求，需要了解保障配置。',
      '您好，保险配置不一定要从“现在买哪一款”开始，更重要的是先看看家庭保障有没有缺口。我们可以一起梳理收入来源、房贷等固定支出、家庭成员的责任，以及现有医疗、重疾和寿险保障，再判断哪些风险需要优先覆盖。保障类产品通常需要长期持有，也要留出足够的现金流，确认这些都合适后再比较具体方案。您目前最想先解决医疗保障、家庭责任，还是长期传承安排？',
      [
        '从家庭责任和保障缺口切入，不借热点制造焦虑',
        '强调长期持有和现金流约束',
        '先做保障梳理，再比较具体产品',
      ],
    ),
  ];
}